import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormRecord,
  Validators,
} from '@angular/forms';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { UserRoles } from '@shared/enums/user-roles';
import { LoaderService } from '@shared/services';
import { forkJoin, of, switchMap } from 'rxjs';
import {
  Collection,
  Country,
  GridDrawerComponent,
  InternalUser,
  InternalUserRole,
  InternalUserWithRoles,
  UserCountryAssignment,
  UserRole,
} from '../../models';
import { InternalUserService, UserRoleService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-upsert-internal-user',
  templateUrl: 'upsert-internal-user.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertInternalUserComponent
  extends GridDrawerComponent<InternalUser, InternalUserWithRoles>
  implements OnInit
{
  private readonly fb = inject(FormBuilder);
  private readonly internalUserService = inject(InternalUserService);
  private readonly userRoleService = inject(UserRoleService);
  private readonly loaderService = inject(LoaderService);
  private readonly cdr = inject(ChangeDetectorRef);

  public form: FormGroup;

  public readonly maxNameLength = 255;
  public userRoles: WritableSignal<UserRole[]> = signal([]);
  enableUpdate: boolean = false;
  countryAdminRoleId?: string;

  get firstNameField(): AbstractControl<string> {
    return this.form.get('firstName')!;
  }

  get lastNameField(): AbstractControl<string> {
    return this.form.get('lastName')!;
  }

  get emailField(): AbstractControl<string> {
    return this.form.get('email')!;
  }

  get countryAssignments(): FormArray<
    FormControl<UserCountryAssignment | null>
  > {
    return this.form.get('countryAssignments') as FormArray<
      FormControl<UserCountryAssignment | null>
    >;
  }

  get userRolesRecord(): FormRecord<FormControl<boolean | null>> {
    return this.form.get('userRoles') as FormRecord<
      FormControl<boolean | null>
    >;
  }

  private prevModel!: InternalUserWithRoles;

  constructor() {
    super();

    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: [
        '',
        [
          Validators.required,
          Validators.required,
          Validators.maxLength(this.maxNameLength),
          Validators.pattern(fieldsValidators.lettersWithSpaces),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.required,
          Validators.maxLength(this.maxNameLength),
          Validators.pattern(fieldsValidators.lettersWithSpaces),
        ],
      ],
      isActive: true,
      userRoles: this.fb.record({}),
      countryAssignments: this.fb.array([]),
      rolesChecked: ['', [Validators.requiredTrue]],
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((values: InternalUserWithRoles) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
            countryAssignments: this.getSelectedCountryAssignments(),
            countryAdminRoleId: this.countryAdminRoleId,
          },
          prevModel: this.prevModel,
          isValid: this.isFormValid(),
        });
      });
  }

  hasRoleSelected(): boolean {
    const hasChecked = this.userRoles().some(
      (role: UserRole) => this.userRolesRecord.controls[role.id].value
    );
    this.form.controls['rolesChecked'].setValue(hasChecked);

    this.updateCountryAdminValidation();

    return hasChecked;
  }

  isCountryAdminSelected(): boolean {
    if (!this.countryAdminRoleId) {
      return false;
    }

    return !!this.userRolesRecord.controls[this.countryAdminRoleId]?.value;
  }

  isCountrySelectionValid(): boolean {
    return !this.isCountryAdminSelected() || this.countryAssignments.length > 0;
  }

  countrySelect(country: Country): void {
    if (this.isCountryAlreadyAdded(country) || !country.countryId) {
      return;
    }

    this.countryAssignments.push(
      this.fb.control({
        countryId: country.countryId,
        name: country.name,
        region: country.region,
        code: country.code,
      })
    );
    this.sortCountryAssignments();
    this.cdr.markForCheck();
  }

  removeCountry(index: number): void {
    this.countryAssignments.removeAt(index);
    this.cdr.markForCheck();
  }

  isCountryAlreadyAdded(country: Country): boolean {
    return this.countryAssignments.value.some(
      (assignment: UserCountryAssignment | null) =>
        assignment?.countryId === country.countryId
    );
  }

  ngOnInit(): void {
    this.initFormData();
  }

  private initFormData(): void {
    this.loaderService.isManualLoading.next(true);
    const currentInternalUserValue = this.model;

    this.userRoleService
      .getInternalUserRoles()
      .pipe(
        switchMap((usersRole: Collection<UserRole>) => {
          this.userRoles.set(usersRole.items);
          this.countryAdminRoleId = usersRole.items.find(
            role => role.name === UserRoles.CountryAdmin
          )?.id;

          this.userRoles().forEach(role => {
            this.userRolesRecord.addControl(role.id, this.fb.control(false));
          });

          if (currentInternalUserValue?.id) {
            return forkJoin({
              userRoles: this.internalUserService.getUserRoles(
                currentInternalUserValue.id
              ),
              countryAssignments:
                this.internalUserService.getCountryAssignments(
                  currentInternalUserValue.id
                ),
            });
          }

          return of(undefined);
        })
      )
      .subscribe(
        (
          loadResult:
            | {
                userRoles: InternalUserRole[];
                countryAssignments: UserCountryAssignment[];
              }
            | undefined
        ) => {
          this.loaderService.isManualLoading.next(false);

          if (!loadResult && !currentInternalUserValue) {
            return;
          }

          const currentUserRoles = loadResult?.userRoles;
          const selectedCountryAssignments =
            loadResult?.countryAssignments ?? [];

          const userRoles = this.userRoles().reduce(
            (rolesMap, userRole) => {
              rolesMap[userRole.id] = !!currentUserRoles?.some(
                role => role.roleId === userRole.id
              );
              return rolesMap;
            },
            {} as Record<string, boolean>
          );

          this.prevModel = {
            ...this.model!,
            userRoles,
            countryAssignments: selectedCountryAssignments,
            countryAdminRoleId: this.countryAdminRoleId,
          };

          this.form.patchValue({
            email: currentInternalUserValue?.email,
            firstName: currentInternalUserValue?.firstName,
            lastName: currentInternalUserValue?.lastName,
            isActive: !!currentInternalUserValue?.isActive,
            userRoles,
            rolesChecked: false,
          });

          this.initCountryAssignments(selectedCountryAssignments);

          if (this.hasRoleSelected()) {
            this.form.controls['rolesChecked'].setValue(true);
          }

          this.cdr.markForCheck();
        }
      );
  }

  private initCountryAssignments(assignments: UserCountryAssignment[]): void {
    this.countryAssignments.clear();
    assignments.forEach(assignment => {
      this.countryAssignments.push(this.fb.control(assignment));
    });
    this.sortCountryAssignments();
  }

  private getSelectedCountryAssignments(): UserCountryAssignment[] {
    return this.countryAssignments.value.filter(
      (assignment): assignment is UserCountryAssignment => !!assignment
    );
  }

  private sortCountryAssignments(): void {
    this.countryAssignments.controls.sort(
      (
        a: FormControl<UserCountryAssignment | null>,
        b: FormControl<UserCountryAssignment | null>
      ) => (a.value?.name ?? '').localeCompare(b.value?.name ?? '')
    );
  }

  private updateCountryAdminValidation(): void {
    if (!this.isCountryAdminSelected()) {
      this.countryAssignments.clear();
    }
  }

  private isFormValid(): boolean {
    const isBaseValid =
      this.isEdit && this.form.valid ? this.enableUpdate : this.form.valid;

    return isBaseValid && this.isCountrySelectionValid();
  }

  private checkChanges(values: InternalUserWithRoles): void {
    if (values && this.prevModel) {
      if (
        values.firstName !== this.prevModel.firstName ||
        values.lastName !== this.prevModel.lastName ||
        values.email !== this.prevModel.email ||
        values.isActive !== this.prevModel.isActive ||
        this.haveCountryAssignmentsChanged(
          this.getSelectedCountryAssignments(),
          this.prevModel.countryAssignments ?? []
        )
      ) {
        this.enableUpdate = true;
        return;
      }

      let hasRoleChanged = false;
      for (const role in values.userRoles) {
        if (this.prevModel.userRoles[role] !== values.userRoles[role]) {
          hasRoleChanged = true;
          break;
        }
      }
      this.enableUpdate = hasRoleChanged;
    }
  }

  private haveCountryAssignmentsChanged(
    currentAssignments: UserCountryAssignment[],
    previousAssignments: UserCountryAssignment[]
  ): boolean {
    const currentIds = currentAssignments
      .map(assignment => assignment.countryId)
      .sort((a, b) => a - b);
    const previousIds = previousAssignments
      .map(assignment => assignment.countryId)
      .sort((a, b) => a - b);

    if (currentIds.length !== previousIds.length) {
      return true;
    }

    return currentIds.some((id, index) => id !== previousIds[index]);
  }
}
